import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import SectionHeader from 'components/SectionHeader';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { useUserGetUserChangeLogs } from 'api/user/user';
import { formatDateTime } from 'utils/date-helpers';
import renderApiValue from 'utils/render-api-value';

/**
 * Historiken har skrivits till UserChangeLogs sedan mars 2026 men har aldrig
 * haft någon läsare i webbadmin. Det här är den vyn: vad som ändrades, när och
 * av vem — så att t.ex. en avstängning för bokning går att följa upp i efterhand.
 */

// Antal rader innan listan viks ihop. Historiken hämtas ofiltrerad från servern,
// så ett långlivat personalkort kan ha hundratals rader.
const COLLAPSED_ROW_COUNT = 15;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 0.9em;
`;

const Th = styled.th`
  text-align: left;
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  vertical-align: top;
`;

const Value = styled.span`
  overflow-wrap: anywhere;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const ShowMore = styled.button`
  align-self: flex-start;
  margin-top: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.tint};
  font: inherit;
`;

interface Props {
  userId: number;
}

const UserChangeLogSection: FC<Props> = ({ userId }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Hämtas först när sektionen öppnas — historiken behövs sällan och kan vara lång.
  const changeLogsQuery = useUserGetUserChangeLogs(
    { appUserId: userId },
    { query: { enabled: expanded } }
  );

  const rows = useMemo(() => changeLogsQuery.data ?? [], [changeLogsQuery.data]);
  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);

  const renderBody = () => {
    if (changeLogsQuery.isLoading) {
      return <LoadingSpinner>Hämtar historik...</LoadingSpinner>;
    }
    if (changeLogsQuery.isError) {
      return <Muted>Kunde inte hämta historiken.</Muted>;
    }
    if (rows.length === 0) {
      return <Muted>Inga registrerade ändringar.</Muted>;
    }

    return (
      <>
        <Table>
          <thead>
            <tr>
              <Th>Tidpunkt</Th>
              <Th>Ändrad av</Th>
              <Th>Fält</Th>
              <Th>Från</Th>
              <Th>Till</Th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((log) => (
              <tr key={log.id}>
                <Td>{formatDateTime(log.created)}</Td>
                <Td>{renderApiValue(log.changedByName)}</Td>
                <Td>{renderApiValue(log.fieldName)}</Td>
                <Td>
                  <Value>{renderApiValue(log.oldValue) || <Muted>–</Muted>}</Value>
                </Td>
                <Td>
                  <Value>{renderApiValue(log.newValue) || <Muted>–</Muted>}</Value>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length > visibleRows.length && (
          <ShowMore onClick={() => setShowAll(true)} type="button">
            Visa alla {rows.length} ändringar
          </ShowMore>
        )}
      </>
    );
  };

  return (
    <Wrapper>
      <Header onClick={() => setExpanded((prev) => !prev)} type="button">
        <FontAwesomeIcon icon={expanded ? faCaretDown : faCaretRight} />
        <SectionHeader>Ändringshistorik</SectionHeader>
      </Header>
      {expanded && renderBody()}
    </Wrapper>
  );
};

export default UserChangeLogSection;
